import React, { useRef, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  StepOneComponent,
  PatientOrgStepThree,
} from "../../../modules/projects/newProject";
import { ModalFooter, ModalBody } from "../../../application/modal";
import { Button, BtnType } from "../../../modules/primitives";
import { useModelPopup } from "../../hooks";
import {
  projectNew,
  clearNewProject,
} from "modules/projects/project/project.action";
import styles from "./newProjectModal.module.scss";
import { shouldUpdateCompanyProjectTasks } from "modules/companies/company/company.action";
import { projectStagesTemplatesGet } from "modules/projects/newProject/stepOne/stepOne.actions";

const NewProjectModal = ({ mode, hideGskFromOptions, selectedIdea }) => {
  const [stepTwo, setStepTwo] = useState(false);
  const [hasBigHeight, setHasBigHeight] = useState(false);
  const [name, setName] = useState(null);
  const [description, setDescription] = useState("");
  const [isPatientOrganization, setIsPatientOrganization] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const popup = useModelPopup();
  const formRef = useRef();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    councilReducer: { selectedCouncil },
    authReducer: { session },
    companiesReducer: {
      companyReducer: { selectedCompany },
    },
    patientOrganizationsReducer: {
      patientOrganizationReducer: { selectedPatientOrganization },
    },
    projectsReducer: {
      projectReducer: { newProjectObject },
    },
    stepOneReducer: { stageTemplates },
  } = useSelector((state) => state);

  useEffect(() => {
    if (!stageTemplates?.length && selectedCouncil) {
      dispatch(
        projectStagesTemplatesGet({
          council_id: selectedCouncil.id,
          enqueueSnackbar,
        })
      );
    }
  }, [selectedCouncil, stageTemplates]);

  useEffect(() => {
    if (selectedIdea) {
      const getProblemTemplate = selectedIdea.ideas_descriptions.find(
        (t) => t.section_name === "Problem Trying to Solve?"
      );

      if (getProblemTemplate) {
        setDescription(getProblemTemplate.section_value);
      } else {
        setDescription(selectedIdea.ideas_descriptions[0]?.section_value);
      }
    }

    return () => null;
  }, [selectedIdea]);

  useEffect(() => {
    if (selectedIdea) {
      setName(selectedIdea.name);
      setIsPatientOrganization(false);
      return;
    }

    if (selectedPatientOrganization.name) {
      setName(selectedPatientOrganization?.name || "");
      setIsPatientOrganization(true);
      return;
    }

    setName(selectedCompany?.name || "");
    setIsPatientOrganization(false);

    return () => null;
  }, [selectedIdea, selectedPatientOrganization, selectedCompany]);

  const handleDismiss = () => {
    popup.hide();
  };

  const hasTemplateOptions = useMemo(() => {
    return (
      stageTemplates?.filter((template) => {
        if (selectedCouncil.name === "GSK") {
          return !template.patient_organization;
        }

        return template;
      })?.length > 0
    );
  }, [stageTemplates, selectedCouncil, mode]);

  const handleCreate = () => {
    if (!stageTemplates) {
      enqueueSnackbar("Please wait for Evaluation Templates to be loaded.", {
        variant: "error",
      });
      return;
    }

    if (!hasTemplateOptions && mode === "modal") {
      enqueueSnackbar(
        "Please ask an Admin to set a Project template as the default.",
        {
          variant: "error",
        }
      );
    }

    const addedCompany = {
      added_by_id: session.id,
      company_id: selectedCompany.id,
    };

    const addedPatientOrganization = {
      added_by_id: session.id,
      gsk_patient_organization_id: selectedPatientOrganization.id,
    };

    const values = formRef?.current?.values;
    let otherProps = {};

    if (selectedIdea?.id) {
      const filteredMembers =
        values.selected_member_ids?.filter((id) => id !== session.id) || [];

      otherProps = {
        select_all_members: values.select_all_members,
        select_all_companies: values.select_all_companies,
        selected_member_ids: filteredMembers,
        selected_company_ids: values.selected_company_ids,
      };
    }

    const getSelectedTemplate =
      stageTemplates.filter(
        (t) => t.id === formRef?.current?.values.stage_template_id
      ) || [];
    const makeStageTemplateObj = {
      val: formRef?.current?.values.stage_template_id,
      obj: getSelectedTemplate,
    };

    const data = {
      ...values,
      ...otherProps,
      councilId: selectedCouncil.id,
      active: true,
      userId: session.id,
      stage_template_id: makeStageTemplateObj,
    };

    if (stepTwo) {
      const newProject = JSON.parse(localStorage.getItem("new_project"));

      const data = {
        ...newProject,
        councilId: selectedCouncil.id,
        active: true,
        userId: session.id,
        enqueueSnackbar,
        stage_template_id: {
          val: newProject.stage_template_id.val,
        },
      };

      if (!selectedIdea) {
        data.organizations = [addedPatientOrganization];
        data.project_companies = [addedCompany];
      } else {
        data.idea_ids = [selectedIdea.id];
      }

      if (newProject.options_score_selected) {
        dispatch(projectNew(data));
      }
    }

    if (!stepTwo && isPatientOrganization) {
      localStorage.setItem("new_project", JSON.stringify(data));
      setStepTwo(true);
    }
    if (!stepTwo && !isPatientOrganization) {
      if (!selectedIdea) {
        data.companies = [addedCompany];
      } else {
        data.idea_ids = [selectedIdea.id];
      }

      dispatch(projectNew(data));
    }
  };

  useEffect(() => {
    localStorage.removeItem("new_project_object");
  }, []);

  useEffect(() => {
    if (newProjectObject) {
      const projObj = JSON.parse(localStorage.getItem("new_project_object"));
      history.push(`/projects/${projObj && projObj.id}/overview`);
      enqueueSnackbar("Project created with success", {
        variant: "success",
      });
      popup.hide();
      dispatch(shouldUpdateCompanyProjectTasks());
      dispatch(clearNewProject());
    }

    return () => localStorage.removeItem("new_project");
  }, [newProjectObject]);

  const handleItemCollapse = (allCollapsed) => {
    setHasBigHeight(allCollapsed);
  };

  const getDefaultModel = useMemo(() => {
    if (selectedIdea?.id) {
      return {
        name,
        idea_id: selectedIdea?.id,
        description,
        selected_member_ids: selectedIdea?.ideas_members?.map(
          (member) => member.user_id
        ),
        selected_company_ids: selectedIdea?.ideas_related_companies?.map(
          (c) => c.company.id
        ),
      };
    }

    return {
      name,
      idea_id: selectedIdea?.id,
      description,
    };
  }, [selectedIdea, name, description]);

  if (!name) return null;

  return (
    <div>
      <ModalBody>
        {stepTwo ? (
          <div style={{ height: hasBigHeight ? 700 : 500 }}>
            <PatientOrgStepThree
              mode={mode}
              isPatientOrganization={isPatientOrganization}
              handleCollapse={handleItemCollapse}
            />
          </div>
        ) : (
          <StepOneComponent
            defultModel={getDefaultModel}
            formRef={formRef}
            mode={mode}
            isPatientOrganization={isPatientOrganization}
            hideGskFromOptions={hideGskFromOptions}
            selectedIdea={selectedIdea}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button btn={BtnType.OUTLINE} onClick={handleDismiss}>
          Cancel
        </Button>
        <Button
          onClick={() => handleCreate()}
          btn={BtnType.HIGHLIGHT}
          className={styles.button}
        >
          {!stepTwo && isPatientOrganization ? "Next Step" : "Create"}
        </Button>
      </ModalFooter>
    </div>
  );
};

export default NewProjectModal;
